/*
 * Account.tsx
 *
 * Features:
 * - Account details page with overview, contacts/notes, tasks, and AI Inbox tabs
 * - Header actions for save, AI summary, calendar, and Gmail workflows
 * - Full-height account notes embedding within the main app shell
 *
 * Logic:
 * - Loads account data from the route id and keeps tab state in sync with the route
 * - Passes account context into overview, notes, task, and AI Inbox surfaces
 * - Uses flex-based sizing so the notes workspace manages its own internal scrolling
 *
 * Recent changes:
 * - Places header actions below the detail tabs on phones, retaining a shared row from tablet widths.
 * - Uses permission-aware Overview schemas consistently across all Account entry paths.
 * - Migrated account detail and My Accounts save state to the shared TanStack Query cache.
 * - Made the tab strip horizontally scroll instead of wrapping on narrow screens.
 * - Added an AI Inbox tab scoped to the current account.
 * - Adopted the shared page rhythm while preserving detail-page horizontal gutters.
 */

import { FC, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { THEME_CLASSES } from '../constants/themeConstants';
import { renderTypeChip } from '../utils/typeChipUtils';
import { useEventBus } from '../context/EventBusContext';
import { EVENTS } from '../constants/eventConstants';
import { useNotification } from '../context/NotificationContext';
import { useTeams } from '../context/TeamContext';
import { SchemaProvider } from '../context/SchemaContext';
import { getCardAnalysisPrompt } from '../utils/summaryUtils';
import { buildGoogleCalendarUrl, buildGmailUrl } from '../utils/googleUtils';
import AccountDetails from '../components/account/AccountDetails';
import AccountTasksPrototype from '../components/account/AccountTasksPrototype';
import ContactNotes from '../components/note/AccountNotes';
import Spinner from '../components/common/Spinner';
import { AIInboxPanel } from './AIInbox';
import PageLayout from '../components/patterns/PageLayout';
import { useAccountQuery, useAddUserAccountMutation } from '../query/accountQueries';
import { normalizePositiveId } from '../query/queryKeys';

const normalizeAccountTab = (tab?: string) => {
  if (tab === 'notes' || tab === 'tasks' || tab === 'ai-inbox') {
    return tab;
  }

  return 'overview';
};

const AccountPage: FC = () => {
  const { id, tab, noteId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => normalizeAccountTab(tab));
  const accountId = normalizePositiveId(id);
  const accountQuery = useAccountQuery(accountId);
  const addUserAccount = useAddUserAccountMutation();
  const account = accountQuery.data ?? null;
  const loading = Boolean(accountId && accountQuery.isPending);
  const error = accountQuery.error ? 'Failed to load account details' : null;

  const { publish } = useEventBus();
  const { currentUserId, isLoading: isLoadingUser } = useTeams();
  const { showNotification } = useNotification();

  const handleSummarize = () => {
    if (!account) return;
    publish(EVENTS.CHAT_OPEN, {
      prompt: getCardAnalysisPrompt({ accountId: account.id }),
      createNewSession: true,
    });
  };

  useEffect(() => {
    setActiveTab(normalizeAccountTab(tab));
  }, [tab]);

  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    navigate(`/accounts/${id}/${newTab}`);
  };

  if (loading) {
    return (
      <div className={`p-6 ${THEME_CLASSES.text.primary}`}>
        <div className="flex items-center justify-center h-64">
          <Spinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-6 ${THEME_CLASSES.text.primary}`}>
        <div className="flex items-center justify-center h-64">
          <div className="text-red-500">{error}</div>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className={`p-6 ${THEME_CLASSES.text.primary}`}>
        <div className="flex items-center justify-center h-64">
          <div>Account not found</div>
        </div>
      </div>
    );
  }


  return (
    <PageLayout className="px-6">
      <header className="flex shrink-0 flex-col gap-4">
        {/* Header section with account name */}
        <h1 className={`text-xl font-semibold ${THEME_CLASSES.text.primary} flex items-center gap-2`}>
          {account.name}
          {renderTypeChip({ rlpCompanyId: account.rlpCompanyId, teamId: account.teamId })}
        </h1>

        {/* Navigation and actions row */}
        <div className="flex flex-col items-stretch gap-2 md:flex-row md:items-center md:justify-between md:gap-3">
        <div className="w-full min-w-0 overflow-x-auto pb-1 md:w-auto md:flex-1">
          <nav className="flex min-w-max flex-nowrap items-center gap-2" aria-label="Tabs">
            <button
              onClick={() => handleTabChange('overview')}
              className={`shrink-0 whitespace-nowrap rounded-full py-1 px-4 text-sm font-medium ${activeTab === 'overview'
                ? `${THEME_CLASSES.button.primary} font-bold`
                : `${THEME_CLASSES.button.secondary} ${THEME_CLASSES.hover.background}`
                }`}
            >
              Overview
            </button>
            <button
              onClick={() => handleTabChange('notes')}
              className={`shrink-0 whitespace-nowrap rounded-full py-1 px-4 text-sm font-medium ${activeTab === 'notes'
                ? `${THEME_CLASSES.button.primary} font-bold`
                : `${THEME_CLASSES.button.secondary} ${THEME_CLASSES.hover.background}`
                }`}
            >
              Contacts and Notes
            </button>
            <button
              onClick={() => handleTabChange('tasks')}
              className={`shrink-0 whitespace-nowrap rounded-full py-1 px-4 text-sm font-medium ${activeTab === 'tasks'
                ? `${THEME_CLASSES.button.primary} font-bold`
                : `${THEME_CLASSES.button.secondary} ${THEME_CLASSES.hover.background}`
                }`}
            >
              Tasks
            </button>
            <button
              onClick={() => handleTabChange('ai-inbox')}
              className={`shrink-0 whitespace-nowrap rounded-full py-1 px-4 text-sm font-medium ${activeTab === 'ai-inbox'
                ? `${THEME_CLASSES.button.primary} font-bold`
                : `${THEME_CLASSES.button.secondary} ${THEME_CLASSES.hover.background}`
                }`}
            >
              AI Inbox
            </button>
          </nav>
        </div>
        {/* Action buttons */}
        <div className="flex shrink-0 items-center gap-2">
          <div className="flex space-x-2">
            <button
              onClick={async () => {
                try {
                  if (isLoadingUser || !currentUserId) {
                    showNotification('User data is still loading', 'error');
                    return;
                  }

                  await addUserAccount.mutateAsync({
                    userId: currentUserId,
                    id: account.id
                  });
                  showNotification(`${account.name} saved to your account list!`, 'success');
                } catch (error) {
                  console.error('Error saving account:', error);
                  showNotification('Failed to save account', 'error');
                }
              }}
              className={`p-2 rounded-md ${THEME_CLASSES.text.secondary} ${THEME_CLASSES.hover.background}`}
              title="Save to your accounts"
              disabled={isLoadingUser || !currentUserId}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </button>
          </div>

          <button
            onClick={handleSummarize}
            className={`p-2 rounded-md ${THEME_CLASSES.text.secondary} ${THEME_CLASSES.hover.background}`}
            title="Ask AI about this account"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </button>

          <button
            onClick={() => {
              const eventTitle = `RE: ${account.name}`;
              const calendarUrl = buildGoogleCalendarUrl(eventTitle, '');
              window.open(calendarUrl, '_blank');
            }}
            className={`p-2 rounded-md ${THEME_CLASSES.text.secondary} ${THEME_CLASSES.hover.background}`}
            title="Add to Google Calendar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </button>

          <button
            onClick={() => {
              const subject = `RE: ${account.name}`;
              const gmailUrl = buildGmailUrl(subject, '');
              window.open(gmailUrl, '_blank');
            }}
            className={`p-2 rounded-md ${THEME_CLASSES.text.secondary} ${THEME_CLASSES.hover.background}`}
            title="Compose Gmail"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </button>
        </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 min-h-0">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 gap-6">
            <SchemaProvider>
              <AccountDetails account={account} />
            </SchemaProvider>
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="h-full min-h-0">
            <ContactNotes
              key={`contact-notes-${account.id}`}
              accountId={parseInt(id!)}
              accountName={account.name}
              initialNoteId={noteId ? parseInt(noteId) : undefined}
              accountTeamId={account.teamId}
              rlpCompanyId={account.rlpCompanyId}
            />
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="h-full min-h-0">
            <AccountTasksPrototype
              accountId={account.id}
              accountName={account.name}
            />
          </div>
        )}

        {activeTab === 'ai-inbox' && (
          <div className="h-full min-h-0">
            <AIInboxPanel
              fixedAccountId={account.id}
              showAccountFilter={false}
            />
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default AccountPage;
